import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { AxiosError } from 'axios';
import { FacebookSDKScript } from '@/components/FacebookSDKScript';
import { FacebookConnectScript } from '@/components/FacebookConnectScript';
import { FACEBOOK_APP_ID } from '@/constants/app';
import { Helmet } from '../../components/Helmet';
import { Layout } from '../../components/Layout';
import { PreloadImage } from '../../components/PreloadImage';
import { ArticleStructuredData, ArticleStructuredDataProps } from '../../components/ArticleStructuredData';
import { BehaviorPageContainer } from '../../containers/BehaviorPageContainer';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { getBehaviorArticle, getBehaviorArticleSlugs } from '../../hooks/api/useGetBehaviorArticle';
import { Routes } from '../../constants/routes';
import { breadcrumbsBuilder } from '../../utils/breadcrumbsBuilder';
import { tryCatch } from '../../utils/tryCatch';
import { CamelizedBehaviorArticleResponse } from '../../types/api/article';
import { isRedirectException } from '../../utils/api/isRedirectException';
import { isPermanentlyRedirectException } from '../../utils/api/isPermanentlyRedirectException';
type BehaviorProps = CamelizedBehaviorArticleResponse & {
  slug: string;
};
const Behavior = (props: BehaviorProps) => {
  const {
    metaTitle,
    metaDesc,
    heroImageUrl,
    structuredData
  } = props;
  const {
    canonicalUrl
  } = useCanonicalUrl();
  return <>
      <Helmet canonical={canonicalUrl} title={metaTitle} description={metaDesc} robots="index, follow" data-sentry-element="Helmet" data-sentry-source-file="[slug].tsx" />

      <PreloadImage src={heroImageUrl} data-sentry-element="PreloadImage" data-sentry-source-file="[slug].tsx" />

      <FacebookSDKScript facebookAppId={FACEBOOK_APP_ID} data-sentry-element="FacebookSDKScript" data-sentry-source-file="[slug].tsx" />
      <FacebookConnectScript data-sentry-element="FacebookConnectScript" data-sentry-source-file="[slug].tsx" />

      <Layout data-sentry-element="Layout" data-sentry-source-file="[slug].tsx">
        <BehaviorPageContainer {...props} data-sentry-element="BehaviorPageContainer" data-sentry-source-file="[slug].tsx" />

        <ArticleStructuredData value={structuredData as ArticleStructuredDataProps['value']} data-sentry-element="ArticleStructuredData" data-sentry-source-file="[slug].tsx" />
      </Layout>
    </>;
};
export const getStaticProps: GetStaticProps<CamelizedBehaviorArticleResponse> = async ({
  params
}) => {
  const slug = String(params.slug);
  const [behavior, err] = await tryCatch<CamelizedBehaviorArticleResponse, AxiosError>(() => getBehaviorArticle(slug));
  if (err) {
    /*
      Assumedly because these guides are fetched
      using the getStaticProps function rather than
      the getServerSideProps function the redirect is
      returned via the error.response here rather than
      via the response object itself
      as in the training guides and other guides.
    */
    const {
      status: statusCode
    } = err.response;
    if (isRedirectException(statusCode)) {
      return {
        redirect: {
          destination: err.response.headers.location,
          permanent: isPermanentlyRedirectException(statusCode)
        }
      };
    }
    return {
      props: {},
      notFound: true,
      // Next.js will default to false if omitted (no revalidation)
      revalidate: 1
    };
  }
  const breadcrumbItems = breadcrumbsBuilder().add({
    title: 'The Daily Wag!',
    url: Routes.FunBlog
  }).add({
    title: 'Behavior',
    url: Routes.KnowledgeBehaviorGuides
  }).add({
    title: behavior.title
  }).build();
  return {
    props: {
      ...behavior,
      slug,
      breadcrumbs: breadcrumbItems
    },
    revalidate: 1
  };
};
export const getStaticPaths: GetStaticPaths = async () => {
  const [slugs, err] = await tryCatch(() => getBehaviorArticleSlugs());
  if (err || !Array.isArray(slugs)) {
    return {
      paths: [],
      fallback: 'blocking'
    };
  }
  const paths = slugs.map(slug => ({
    params: {
      slug
    }
  }));
  return {
    paths,
    fallback: 'blocking'
  };
};
export default Behavior;